// Background Job Processing & Analytics Pipeline
// Netflix/Duolingo-style background processing for heavy operations
// Enhanced with batch processing and request tracing

import { logger } from '../utils/logger';
import { batchProcessor } from './batchProcessor';
import { requestTracing } from './requestTracing';

export interface Job {
  id: string;
  type: 'streak_calculation' | 'leaderboard_update' | 'analytics' | 'notification' | 'recommendation_update';
  userId?: string;
  data?: any;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: string;
  processedAt?: string;
  error?: string;
}

export interface AnalyticsEvent {
  userId: string;
  eventType: string;
  eventData: any;
  timestamp: string;
}

class BackgroundJobsService {
  private jobQueue: Job[] = [];
  private processing: boolean = false;
  private eventQueue: AnalyticsEvent[] = [];

  constructor() {
    // Load queued jobs from localStorage
    this.loadJobQueue();
    this.loadEventQueue();

    // Start processing jobs
    this.startJobProcessor();

    // Start analytics pipeline
    this.startAnalyticsPipeline();

    // Schedule periodic jobs
    this.schedulePeriodicJobs();
  }

  // Queue a job for background processing
  async queueJob(job: Omit<Job, 'id' | 'status' | 'createdAt'>): Promise<string> {
    const jobWithMeta: Job = {
      ...job,
      id: this.generateJobId(),
      status: 'pending',
      createdAt: new Date().toISOString()
    };

    this.jobQueue.push(jobWithMeta);
    this.saveJobQueue();

    // Try to process immediately if not already processing
    if (!this.processing) {
      this.processJobQueue();
    }

    return jobWithMeta.id;
  }

  // Process job queue
  private async processJobQueue(): Promise<void> {
    if (this.processing || this.jobQueue.length === 0) {
      return;
    }

    this.processing = true;

    // Sort by priority (high first)
    this.jobQueue.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });

    while (this.jobQueue.length > 0) {
      const job = this.jobQueue.shift();
      if (!job) break;

      try {
        job.status = 'processing';
        this.saveJobQueue();

        await this.executeJob(job);

        job.status = 'completed';
        job.processedAt = new Date().toISOString();
        logger.debug(`Job ${job.id} completed successfully`);
      } catch (error: any) {
        job.status = 'failed';
        job.error = error.message;
        job.processedAt = new Date().toISOString();
        logger.error(`Job ${job.id} failed:`, error);
      }

      this.saveJobQueue();
    }

    this.processing = false;
  }

  // Execute a job
  private async executeJob(job: Job): Promise<void> {
    switch (job.type) {
      case 'streak_calculation':
        await this.calculateStreak(job.userId!);
        break;
      case 'leaderboard_update':
        await this.updateLeaderboard();
        break;
      case 'analytics':
        await this.processAnalytics({ ...job.data, userId: job.userId });
        break;
      case 'notification':
        await this.sendNotification(job.userId!, job.data);
        break;
      case 'recommendation_update':
        await this.updateRecommendations(job.userId!);
        break;
      default:
        throw new Error(`Unknown job type: ${job.type}`);
    }
  }

  // Calculate streak for a user (background job)
  private async calculateStreak(userId: string): Promise<void> {
    // This would integrate with gamificationService
    // For now, we'll just log it
    logger.debug(`Calculating streak for user ${userId}`);
    
    // TODO: Integrate with gamificationService
    // const streak = await gamificationService.calculateStreak(userId);
    // await this.saveStreakToCloud(userId, streak);
  }

  // Update leaderboard (background job)
  private async updateLeaderboard(): Promise<void> {
    logger.debug('Updating leaderboard...');
    
    // TODO: Fetch all users, calculate rankings, update leaderboard
    // This would be a heavy operation, so it runs in background
  }

  // Process analytics event
  private async processAnalytics(data: any): Promise<void> {
    // Queue for batch processing
    this.eventQueue.push({
      userId: data.userId,
      eventType: data.eventType,
      eventData: data.eventData,
      timestamp: new Date().toISOString()
    });

    this.saveEventQueue();
  }

  // Send notification (background job)
  private async sendNotification(userId: string, _data: any): Promise<void> {
    logger.debug(`Sending notification to user ${userId}`);
    
    // TODO: Integrate with notificationService
    // await notificationService.send(userId, data);
  }

  // Update recommendations (background job)
  private async updateRecommendations(userId: string): Promise<void> {
    logger.debug(`Updating recommendations for user ${userId}`);
    
    // TODO: Integrate with aiCoach to recalculate recommendations
  }

  // Track analytics event
  trackEvent(userId: string, eventType: string, eventData: any): void {
    // Queue for background processing (non-blocking)
    this.queueJob({
      type: 'analytics',
      userId,
      data: { eventType, eventData },
      priority: 'low'
    }).catch(err => {
      logger.error('Error queueing analytics event:', err);
    });
  }

  // Start job processor
  private startJobProcessor(): void {
    // Process jobs every 5 seconds
    setInterval(() => {
      if (!this.processing && this.jobQueue.length > 0) {
        this.processJobQueue();
      }
    }, 5000);

    // Also process immediately if queue has high-priority jobs
    if (this.jobQueue.some(job => job.priority === 'high')) {
      this.processJobQueue();
    }
  }

  // Start analytics pipeline
  private startAnalyticsPipeline(): void {
    // Batch process analytics events every 30 seconds
    setInterval(() => {
      this.flushAnalyticsEvents();
    }, 30000);
  }

  // Flush analytics events to cloud
  // Enhanced with batch processing (Netflix/Duolingo style)
  private async flushAnalyticsEvents(): Promise<void> {
    if (this.eventQueue.length === 0) return;

    return await requestTracing.trace('flushAnalyticsEvents', async () => {
      const events = [...this.eventQueue];
      this.eventQueue = [];
      this.saveEventQueue();

      try {
        // RLS requires a real authenticated user id (not "anonymous")
        const toWrite = events.filter((e) => e.userId && e.userId !== 'anonymous');
        for (const event of toWrite) {
          await batchProcessor.addToBatch(
            'analytics_events',
            {
              user_id: event.userId,
              event_type: event.eventType,
              event_data: JSON.stringify(event.eventData),
              timestamp: event.timestamp
            },
            'insert'
          );
        }

        // Force flush to ensure events are saved
        await batchProcessor.forceFlush();

        logger.debug(`[Batch] Queued ${events.length} analytics events for batch processing`);
      } catch (error) {
        logger.error('Error queueing analytics events for batch processing:', error);
        // Re-queue on error
        this.eventQueue.push(...events);
        this.saveEventQueue();
      }
    }, { eventCount: this.eventQueue.length });
  }

  // Schedule periodic jobs
  private schedulePeriodicJobs(): void {
    // Daily streak calculation (midnight)
    this.scheduleDailyJob('00:00', () => {
      this.queueJob({
        type: 'streak_calculation',
        priority: 'medium',
        data: { allUsers: true }
      });
    });

    // Weekly leaderboard update (Monday 00:00)
    this.scheduleWeeklyJob(1, '00:00', () => {
      this.queueJob({
        type: 'leaderboard_update',
        priority: 'high'
      });
    });
  }

  // Schedule daily job
  private scheduleDailyJob(time: string, callback: () => void): void {
    const [hours, minutes] = time.split(':').map(Number);
    const now = new Date();
    const scheduled = new Date();
    scheduled.setHours(hours, minutes, 0, 0);

    if (scheduled <= now) {
      scheduled.setDate(scheduled.getDate() + 1);
    }

    const delay = scheduled.getTime() - now.getTime();

    setTimeout(() => {
      callback();
      // Schedule next occurrence
      setInterval(callback, 24 * 60 * 60 * 1000);
    }, delay);
  }

  // Schedule weekly job
  private scheduleWeeklyJob(dayOfWeek: number, time: string, callback: () => void): void {
    const [hours, minutes] = time.split(':').map(Number);
    const now = new Date();
    const scheduled = new Date();
    scheduled.setHours(hours, minutes, 0, 0);

    // Find next occurrence of dayOfWeek (0 = Sunday, 1 = Monday, etc.)
    const daysUntil = (dayOfWeek - scheduled.getDay() + 7) % 7;
    if (daysUntil === 0 && scheduled <= now) {
      scheduled.setDate(scheduled.getDate() + 7);
    } else {
      scheduled.setDate(scheduled.getDate() + daysUntil);
    }

    const delay = scheduled.getTime() - now.getTime();

    setTimeout(() => {
      callback();
      // Schedule next occurrence
      setInterval(callback, 7 * 24 * 60 * 60 * 1000);
    }, delay);
  }

  // Generate job ID
  private generateJobId(): string {
    return `job_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Save job queue to localStorage
  private saveJobQueue(): void {
    try {
      localStorage.setItem('backgroundJobQueue', JSON.stringify(this.jobQueue));
    } catch (error) {
      logger.error('Error saving job queue:', error);
    }
  }

  // Load job queue from localStorage
  private loadJobQueue(): void {
    try {
      const saved = localStorage.getItem('backgroundJobQueue');
      if (saved) {
        this.jobQueue = JSON.parse(saved);
        // Filter out completed jobs older than 24 hours
        const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
        this.jobQueue = this.jobQueue.filter(job => {
          if (job.status === 'completed' && job.processedAt) {
            return new Date(job.processedAt).getTime() > oneDayAgo;
          }
          return true;
        });
        this.saveJobQueue();
      }
    } catch (error) {
      logger.error('Error loading job queue:', error);
    }
  }

  // Save event queue to localStorage
  private saveEventQueue(): void {
    try {
      localStorage.setItem('analyticsEventQueue', JSON.stringify(this.eventQueue));
    } catch (error) {
      logger.error('Error saving event queue:', error);
    }
  }

  // Load event queue from localStorage
  private loadEventQueue(): void {
    try {
      const saved = localStorage.getItem('analyticsEventQueue');
      if (saved) {
        this.eventQueue = JSON.parse(saved);
      }
    } catch (error) {
      logger.error('Error loading event queue:', error);
    }
  }

  // Get job status
  getJobStatus(jobId: string): Job | null {
    return this.jobQueue.find(job => job.id === jobId) || null;
  }

  // Get queue statistics
  getQueueStats(): {
    pending: number;
    processing: number;
    completed: number;
    failed: number;
  } {
    return {
      pending: this.jobQueue.filter(j => j.status === 'pending').length,
      processing: this.jobQueue.filter(j => j.status === 'processing').length,
      completed: this.jobQueue.filter(j => j.status === 'completed').length,
      failed: this.jobQueue.filter(j => j.status === 'failed').length
    };
  }
}

export const backgroundJobs = new BackgroundJobsService();

